import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import { EventServices } from '@/middlewares/services'
import useIsPublished, { PUBLISH_TYPES } from '@/hooks/useIsPublished'
import useReservationRefresh from '@/hooks/useReservationRefresh'
import useScreenTop from '@/hooks/useScreenTop'
import { useOrganizerShell } from '@/components/Organizer/OrganizerShell/OrganizerShellContext'
import { useAuth } from '@/middlewares/auth/auth-context'
import { useError } from '@/contexts/error-context'
import LoadingScreen from '@/components/common/LoadingScreen'
import { ConvertLeaderboard, ConvertPresentation } from '@/components/EventPage/index.utils'

import EventHubProvider from './EventHubContext'
import EventHubHero from './EventHubHero'
import EventHubPartners from './EventHubPartners'
import EventHubTabNav from './EventHubTabNav'
import EventHubRegBar from './EventHubRegBar'
import EventHubSidebar from './EventHubSidebar'
import EventHubSidebarMap from './EventHubSidebarMap'
import EventHubSettingsSidebar from './settings/EventHubSettingsSidebar'
import { EVENT_HUB_TABS, isFullWidthTabPath } from './tabs.config'

export default function EventHub() {
    const { eventIdentifier } = useParams()
    const { pathname } = useLocation()
    const errorContext = useError()
    const authContext = useAuth()

    // Tabs flagged fullWidth (leaderboard) take the whole row: no sticky side panel, no map.
    const isFullWidth = isFullWidthTabPath(pathname)

    const [canEdit, isCheckingPublished] = useIsPublished(eventIdentifier, PUBLISH_TYPES.EVENT)

    const [competition, setCompetition] = useState()
    const [presentation, setPresentation] = useState()
    const [isLoading, setIsLoading] = useState(false)
    // Media edit the gallery without global canEdit; same "don't clobber edits" rule as organizers.
    const isMediaAccount = authContext.isMediaAccount(competition?.mediaAccounts)

    // Organizer shell (side menu): the Status block is the truth for the five publish flags; the hub
    // mirrors them so the hero / sidebar follow a toggle ("Registration Closed" when Ticketing is off).
    const shell = useOrganizerShell()
    const shellStatus = shell?.status
    useEffect(() => {
        if (!shellStatus || !canEdit) return
        setPresentation((previous) => (previous ? { ...previous, publish: shellStatus.event } : previous))
        setCompetition((previous) =>
            previous
                ? {
                      ...previous,
                      ticketingIsPublished: shellStatus.ticketing,
                      _ticketing: { ...(previous._ticketing || {}), publish: shellStatus.ticketing },
                      planningIsPublished: shellStatus.planning,
                      publish: shellStatus.leaderboard,
                      _volunteering: previous._volunteering
                          ? { ...previous._volunteering, publish: shellStatus.volunteering }
                          : previous._volunteering,
                      volunteering: previous.volunteering
                          ? { ...previous.volunteering, publish: shellStatus.volunteering }
                          : previous.volunteering,
                  }
                : previous,
        )
    }, [shellStatus, canEdit])

    // Screen change (tab, mode): scroll back so the tab bar reaches its stuck position, the banner
    // just out of view (mock screenTop with the tab bar as anchor); never down.
    const activeTab =
        EVENT_HUB_TABS.find((tab) => tab.path && pathname.replace(/\/+$/, '').endsWith(`/${tab.path}`))?.key ||
        EVENT_HUB_TABS[0].key
    const tabNavRef = useRef(null)
    useScreenTop(competition ? `hub:${canEdit ? 'org' : 'public'}:${activeTab}` : null, () => tabNavRef.current)

    const getEvent = useCallback(
        async ({ silent = false } = {}) => {
            try {
                if (!silent) setIsLoading(true)
                // Organizers (and admins) get the unfiltered presentation so they can
                // see and edit unpublished content — same split as the old EventPage.
                const res = await (canEdit
                    ? EventServices.getPresentationByEventIdentifier
                    : EventServices.getPublicPresentationByEventIdentifier)(eventIdentifier)
                const results = res.data
                setPresentation(ConvertPresentation(results.presentation, results.leaderboard))
                setCompetition(ConvertLeaderboard(results))
                localStorage.setItem('orgaId', results.leaderboard.orgaId)
            } catch (error) {
                console.log('/EventHub getEvent - error', { error })
                if (error.response?.status === 404) {
                    errorContext.setError({ type: 404 })
                }
            } finally {
                if (!silent) setIsLoading(false)
            }
        },
        [canEdit, eventIdentifier],
    )

    useEffect(() => {
        if (!eventIdentifier) {
            console.error('EventHub: missing eventIdentifier')
            return
        }
        if (!isCheckingPublished) {
            getEvent()
        }
    }, [isCheckingPublished])

    // Public page: spot counters follow bookings and cancellations live (trend arrows on the
    // division cards). Not while editing — a silent refetch would clobber unsaved organizer
    // or media gallery edits. The whole presentation is refetched, so this page waits longer
    // than the registration form: 3 s + jitter keeps the open event pages of a rush from
    // refetching it in the same second.
    useReservationRefresh(() => getEvent({ silent: true }), {
        competitionId: competition?._id,
        delay: 3000,
        enabled: !canEdit && !isMediaAccount && !isCheckingPublished && Boolean(competition),
    })

    if (isCheckingPublished || !competition) {
        return <LoadingScreen />
    }

    const contextValue = {
        eventIdentifier,
        competition,
        presentation,
        canEdit,
        isLoading,
        setCompetition,
        setPresentation,
        setIsLoading,
    }

    return (
        <EventHubProvider value={contextValue}>
            {/* --eh-sidebar-w = mock aside (337) + its 34px right inset (`.ev-cols{padding-right}`) */}
            <div className="[--eh-sidebar-w:371px]">
                <Helmet>
                    <title>{competition.name}</title>
                </Helmet>
                {isLoading && <LoadingScreen />}
                <EventHubHero />
                <EventHubTabNav fullWidth={isFullWidth} stickyRef={tabNavRef} />
                {/* Mobile fixed registration bar (mock #regBar): never for organizers, never on the
                    leaderboard (its own docked controls own the bottom of the screen). */}
                {!canEdit && !isFullWidth && <EventHubRegBar />}
                {isFullWidth ? (
                    <main className="min-h-[240px] min-w-0 max-nav:px-(--x004) max-nav:pb-(--x009) max-nav:pt-(--x004) nav:px-(--x006)">
                        <Suspense fallback={<LoadingScreen />}>
                            <Outlet />
                        </Suspense>
                    </main>
                ) : (
                    /* Two columns only ≥ cols (1101): below, as in the mock ≤1100, the card leaves the banner and
                       comes under the tab bar (banner → tabs → card → content), the fixed bar taking over its CTA. */
                    <div className="flex min-h-[240px] flex-col gap-(--x004) pt-(--x004) max-nav:px-(--x004) max-cols:pb-(--x009) nav:px-(--x006) cols:grid cols:grid-cols-[1fr_var(--eh-sidebar-w)] cols:items-start cols:gap-[70px] cols:pt-0">
                        {/* z-30: paints over the sticky tab bar's white curtain (z-20) when both are pinned.
                            -266px: the aside overlaps the banner by 164px in the mock (`.ev-aside{margin-top:-180px}`
                            over a 16px gap) — the grid starts 16 + 72 (tab bar) + 14 under the banner. */}
                        <div className="cols:sticky cols:top-3 cols:mr-[34px] cols:z-30 cols:-mt-[266px]">
                            {canEdit ? <EventHubSettingsSidebar /> : <EventHubSidebar />}
                        </div>
                        <main className="min-w-0 cols:order-first">
                            {/* Partners strip under the tab bar on every tab but the leaderboard (mock .ev-partners). */}
                            <EventHubPartners partners={presentation?.partners} />
                            <Suspense fallback={<LoadingScreen />}>
                                <Outlet />
                            </Suspense>
                        </main>
                        {!canEdit && <EventHubSidebarMap className="nav:hidden pt-(--x001)" />}
                    </div>
                )}
            </div>
        </EventHubProvider>
    )
}
